import type {
  AnalyticsEventName,
  AnalyticsVendor,
  PickAnalyticsEventAttributes,
} from '../../../types'
import { eventFactoryCollection } from '../event-factory'
import {
  CLIENT_ID_TTL,
  PERSEUS_CLIENT_ID_STORE_KEY,
  PERSEUS_SESSION_STORE_KEY,
  SESSION_TTL,
} from './constants'
import {
  generateUniqueId,
  getLocationDataFromGlobalAttributes,
  isValidEvent,
  isValidId,
  isValidPerseusSession,
} from './helpers'
import type { PerseusConsent, PerseusSession } from './types'
import { getCookieConsentStatus, onCookiesAccepted } from '@/tools/cookies-blocker/service'
import { serviceConsentCategory } from '@/tools/cookies-blocker/service/constants'
import type { CookieCategory } from '@/tools/cookies-blocker/types'
import { storage } from '@/tools/storage'

export class Perseus implements AnalyticsVendor {
  public name = 'Perseus'
  public consentCategory: CookieCategory = serviceConsentCategory.perseus
  public isInitialized = false

  private eventEmissionCount: number = 0
  private consentLevel: PerseusConsent = 'essential'
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private globalAttributes: Record<string, unknown> = {}

  public async init() {
    void onCookiesAccepted('essential').then(this.evaluateConsentLevel.bind(this))
    void onCookiesAccepted('functional').then(this.evaluateConsentLevel.bind(this))
    void onCookiesAccepted('marketing').then(this.evaluateConsentLevel.bind(this))
    this.isInitialized = true
  }

  public setGlobalAttribute(key: string, value: unknown) {
    this.globalAttributes[key] = value
  }

  public canHandle = (eventName: AnalyticsEventName) =>
    Object.keys(eventFactoryCollection).includes(eventName)

  public trackEvent<EVENT_NAME extends AnalyticsEventName>(
    eventName: EVENT_NAME,
    attributes: PickAnalyticsEventAttributes<EVENT_NAME>
  ) {
    const factoryMethod = eventFactoryCollection[eventName as keyof typeof eventFactoryCollection]
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const eventInstance = factoryMethod(attributes as any)

    if (!isValidEvent(eventInstance)) {
      throw new Error(`[Perseus]: Invalid event instance: ${eventName}`)
    }

    this.updateExpirationDates()

    const location = getLocationDataFromGlobalAttributes(this.globalAttributes)
    const country = location?.locationCountry || null
    const userId = this.globalAttributes.userId || null

    const baseAttributes = {
      dhhPerseusGuestId: this.getClientId(),
      dhhPerseusSessionId: this.getSession().id,
      globalEntityId: country ? `GV_${country.toUpperCase()}` : null,
      dhhPerseusHitId: generateUniqueId(),
      hitNumber: this.eventEmissionCount++,
      payloadTimestamp: new Date().toISOString(),
      consent: this.consentLevel,
      userId,
    }

    const dataLayerEvent = {
      ...baseAttributes,
      event: eventInstance.name,
      perseusEventProperties: {
        pageUrlPath: window.location.pathname,
        ...location,
        ...eventInstance.attributes,
      },
    }

    if (!window.dataLayer || !Array.isArray(window.dataLayer)) {
      throw new Error('[Perseus]: GTM dataLayer is not defined')
    }

    window.dataLayer.push(
      dataLayerEvent,
      // https://developers.google.com/tag-platform/devguides/datalayer#set
      // Event variables are shared between all events
      // We need to cleanup all the perseus variables after the event
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      function (this: any) {
        const eventVariables = Object.keys(dataLayerEvent)
        eventVariables.forEach((variable) => this.set(variable, undefined))
      }
    )
  }

  private async evaluateConsentLevel() {
    const status = await getCookieConsentStatus()
    if (status.essential && status.marketing && status.functional) {
      this.consentLevel = 'essential_functional_marketing'
    } else if (status.essential && status.functional) {
      this.consentLevel = 'essential_functional'
    } else if (status.essential && status.marketing) {
      this.consentLevel = 'essential_marketing'
    } else if (status.essential) {
      this.consentLevel = 'essential'
    }
  }

  private getClientId(): string {
    const clientId = storage.get(PERSEUS_CLIENT_ID_STORE_KEY)
    if (isValidId(clientId)) {
      return clientId
    }

    const newClientId = generateUniqueId()
    const expirationDate = new Date().getTime() + CLIENT_ID_TTL
    storage.set(PERSEUS_CLIENT_ID_STORE_KEY, newClientId, expirationDate)
    return newClientId
  }

  private getSession(): PerseusSession {
    const perseusSession = storage.get(PERSEUS_SESSION_STORE_KEY)
    if (isValidPerseusSession(perseusSession)) {
      return perseusSession
    }
    const session: PerseusSession = { id: generateUniqueId(), timestamp: Date.now() }
    const expirationDate = session.timestamp + SESSION_TTL
    storage.set(PERSEUS_SESSION_STORE_KEY, session, expirationDate)
    return session
  }

  private updateExpirationDates() {
    storage.set(PERSEUS_SESSION_STORE_KEY, this.getSession(), Date.now() + SESSION_TTL)
    storage.set(PERSEUS_CLIENT_ID_STORE_KEY, this.getClientId(), Date.now() + CLIENT_ID_TTL)
  }
}
